import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CreditCard, DollarSign } from 'lucide-react';
import { accountsPayableService } from '@/services/accountsPayableService';
import type { AccountPayable, PaymentMethod } from '@/types/accounts-payable.types';

interface PaymentModalProps {
  account: AccountPayable;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (paymentData: {
    amount: number;
    paymentMethod: PaymentMethod;
    paymentDate: string;
    reference?: string;
    notes?: string;
  }) => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  account,
  isOpen,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: '' as PaymentMethod,
    paymentDate: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentMethods: { value: PaymentMethod; label: string }[] = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'check', label: 'Cheque' },
    { value: 'bank_transfer', label: 'Transferencia Bancaria' },
    { value: 'credit_card', label: 'Tarjeta de Crédito' },
    { value: 'debit_card', label: 'Tarjeta de Débito' },
    { value: 'other', label: 'Otro' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'El monto debe ser mayor a 0';
    } else if (parseFloat(formData.amount) > account.pendingAmount) {
      newErrors.amount = 'El monto no puede ser mayor al saldo pendiente';
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Debe seleccionar un método de pago';
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Debe seleccionar una fecha de pago';
    }

    if (['check', 'bank_transfer'].includes(formData.paymentMethod) && !formData.reference) {
      newErrors.reference = 'La referencia es requerida para este método de pago';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        paymentDate: formData.paymentDate,
        reference: formData.reference || undefined,
        notes: formData.notes || undefined
      });
      
      // Reset form
      setFormData({
        amount: '',
        paymentMethod: '' as PaymentMethod,
        paymentDate: new Date().toISOString().split('T')[0],
        reference: '',
        notes: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (value: string) => {
    // Solo permitir números y punto decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, amount: numericValue }));
    
    // Limpiar error de monto si existe
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const setFullPayment = () => {
    setFormData(prev => ({ 
      ...prev, 
      amount: account.pendingAmount.toString() 
    }));
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const isOverdue = accountsPayableService.isOverdue(account);
  const paymentAmount = parseFloat(formData.amount) || 0;
  const isPartialPayment = paymentAmount > 0 && paymentAmount < account.pendingAmount;
  const isFullPayment = paymentAmount === account.pendingAmount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Registrar Pago - {account.accountNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información de la cuenta */}
          <Card>
            <CardContent className="space-y-3 pt-4">
              <div>
                <Label className="text-xs text-gray-500">Proveedor</Label>
                <p className="font-medium">{account.supplierName}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Monto Recibido</Label>
                  <p className="font-medium text-green-600">
                    {accountsPayableService.formatCurrency(account.receivedAmount)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Monto Pagado</Label>
                  <p className="font-medium text-blue-600">
                    {accountsPayableService.formatCurrency(account.paidAmount)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Saldo Pendiente</Label>
                  <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-yellow-600'}`}>
                    {accountsPayableService.formatCurrency(account.pendingAmount)}
                  </p>
                </div>
              </div>

              {account.dueDate && (
                <div>
                  <Label className="text-xs text-gray-500">Fecha de Vencimiento</Label>
                  <p className={`text-sm ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
                    {accountsPayableService.formatDate(account.dueDate)}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Badge 
                  className={accountsPayableService.getStatusColor(account.status)}
                  variant="secondary"
                >
                  {accountsPayableService.getStatusText(account.status)}
                </Badge>
                {isOverdue && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Vencida
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Formulario de pago */}
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Monto */}
              <div className="space-y-2">
                <Label htmlFor="amount">
                  Monto del Pago <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="amount"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className={`pl-10 ${errors.amount ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.amount && (
                  <p className="text-sm text-red-500">{errors.amount}</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={setFullPayment}
                  className="w-full"
                >
                  Pago Completo ({accountsPayableService.formatCurrency(account.pendingAmount)})
                </Button>
              </div>

              {/* Método de pago */}
              <div className="space-y-2">
                <Label htmlFor="paymentMethod">
                  Método de Pago <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.paymentMethod} 
                  onValueChange={(value: PaymentMethod) => {
                    setFormData(prev => ({ ...prev, paymentMethod: value }));
                    if (errors.paymentMethod) {
                      setErrors(prev => ({ ...prev, paymentMethod: '' }));
                    }
                  }}
                >
                  <SelectTrigger className={errors.paymentMethod ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.paymentMethod && (
                  <p className="text-sm text-red-500">{errors.paymentMethod}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Fecha de pago */}
              <div className="space-y-2">
                <Label htmlFor="paymentDate">
                  Fecha de Pago <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, paymentDate: e.target.value }));
                    if (errors.paymentDate) {
                      setErrors(prev => ({ ...prev, paymentDate: '' }));
                    }
                  }}
                  className={errors.paymentDate ? 'border-red-500' : ''}
                />
                {errors.paymentDate && (
                  <p className="text-sm text-red-500">{errors.paymentDate}</p>
                )}
              </div>

              {/* Referencia */}
              <div className="space-y-2">
                <Label htmlFor="reference">
                  Referencia 
                  {['check', 'bank_transfer'].includes(formData.paymentMethod) && (
                    <span className="text-red-500">*</span>
                  )}
                </Label>
                <Input
                  id="reference"
                  placeholder="Número de cheque, transferencia, etc."
                  value={formData.reference}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, reference: e.target.value }));
                    if (errors.reference) {
                      setErrors(prev => ({ ...prev, reference: '' }));
                    }
                  }}
                  className={errors.reference ? 'border-red-500' : ''}
                />
                {errors.reference && (
                  <p className="text-sm text-red-500">{errors.reference}</p>
                )}
              </div>
            </div>

            {/* Notas */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notas Adicionales</Label>
              <Textarea
                id="notes"
                placeholder="Información adicional sobre el pago..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          {/* Resumen del pago */}
          {paymentAmount > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-blue-900">Resumen del Pago</h4>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">Monto del pago:</span>
                      <span className="float-right font-medium text-blue-900">
                        {accountsPayableService.formatCurrency(paymentAmount)}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700">Saldo después del pago:</span>
                      <span className="float-right font-medium text-blue-900">
                        {accountsPayableService.formatCurrency(account.pendingAmount - paymentAmount)}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-blue-200">
                    {isFullPayment && (
                      <p className="text-green-700 font-medium">
                        ✓ Este pago liquidará completamente la cuenta
                      </p>
                    )}
                    {isPartialPayment && (
                      <p className="text-yellow-700 font-medium">
                        ⚠ Este será un pago parcial
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !formData.amount || !formData.paymentMethod}
          >
            {isSubmitting ? 'Registrando...' : 'Registrar Pago'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};